import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import api from "@/lib/api";
import Eyebrow from "@/components/Eyebrow";
import SEO from "@/components/SEO";
import { ArrowLeft, Loader2 } from "lucide-react";

const fmtDate = (v) => {
  try {
    return new Date(v).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
};

export default function NewsDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api
      .get(`/news/${slug}`)
      .then((r) => setPost(r.data))
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
      });
  }, [slug]);

  if (notFound) return <Navigate to="/news" replace />;

  if (!post) {
    return (
      <section className="py-32 min-h-[60vh] flex items-center justify-center">
        <Loader2 className="animate-spin text-palm" size={32} />
      </section>
    );
  }

  return (
    <>
      <SEO
        title={post.title}
        description={post.excerpt}
        image={post.cover_url || undefined}
        path={`/news/${post.slug}`}
        type="article"
      />
      <section className="pt-16 pb-10 bg-paper">
        <div className="max-w-[860px] mx-auto px-6">
          <Link to="/news" className="inline-flex items-center gap-2 text-ink/60 no-underline text-sm mb-8 hover:text-palm" data-testid="news-back-btn">
            <ArrowLeft size={16} /> All stories
          </Link>
          <Eyebrow>{post.tag || "HOFAVIPROC"}</Eyebrow>
          <h1 className="text-[clamp(34px,4.5vw,54px)] font-display font-bold mb-4 leading-[1.08]">
            {post.title}
          </h1>
          <p className="text-ink/70 text-sm">{fmtDate(post.created_at)}</p>
        </div>
      </section>

      {post.cover_url && (
        <section className="pb-8">
          <div className="max-w-[1000px] mx-auto px-6">
            <div className="aspect-[16/9] rounded-[14px] overflow-hidden bg-ink/5">
              <img src={post.cover_url} alt="" className="w-full h-full object-cover" />
            </div>
          </div>
        </section>
      )}

      <section className="pb-24">
        <div className="max-w-[720px] mx-auto px-6" data-testid={`news-detail-${post.slug}`}>
          <p className="text-[19px] text-ink/85 leading-relaxed mb-8 font-medium">
            {post.excerpt}
          </p>
          <div className="prose max-w-none">
            {post.body.split("\n\n").map((para, i) => (
              <p key={i} className="mb-5 text-ink/85 text-[17px] leading-[1.75]">{para}</p>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-ink/10 flex flex-wrap gap-3">
            <Link to="/get-involved#donate" className="bg-palm text-white font-bold rounded-full px-6 py-3 no-underline hover:shadow-xl transition">Support HOFA</Link>
            <Link to="/news" className="border-2 border-ink/15 text-ink font-bold rounded-full px-6 py-3 no-underline hover:border-palm hover:text-palm transition">More stories</Link>
          </div>
        </div>
      </section>
    </>
  );
}
